/**
 * @Author: v_jppjpeng
 * @Date: 2023-09-20 21:50:01
 * @Explain: 分页下拉选择
 */
import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PopupVisibleChangeContext, SelectProps } from 'tdesign-react';
import { Popup, Tabs } from 'tdesign-react';
// utils
import { setTimeOut, throttle } from '@/utils/common';
import { getDefaultValueByType } from '@/utils/common/dataConversion';
import { isArrayLen } from '@/utils/common/dataJudgment';
import { onSelectRenderText } from '../const';
// type
import type {
  PagingSelectProps,
  ScrollListProps,
  SelectPopupProps,
} from '../data.d';
import { InputSizeEnum } from '../data.d';
// components
import ScrollList from '../scrollList';
import SelectCustom from '../select/custom';

type PropsType = {
  /** tabs */
  tabsList?: {
    label: string;
    value: string;
    disabled?: boolean;
    selectProps: any;
  }[];
  /** 下拉props */
  selectProps?: SelectProps;
  label?: ReactNode | string;
} & PagingSelectProps &
  ScrollListProps &
  SelectPopupProps;

function PagingSelect(props: PropsType, refs: any) {
  const {
    className,

    value,
    placeholder = intlGet('COMMON.PLEASE.SELECT'),
    searchPlaceholder = intlGet('COMMON.PLEASE.ENTER'),
    disabled,
    searchable,
    clearable,
    size = InputSizeEnum.M,
    borderless = false,
    autoWidth = false,
    matchButtonWidth = true,
    ellipsis = true,
    autofocus = false,
    listHeader,
    footer,
    tabsList = [],
    isAutoRequest,
    defaultVisible = false,
    defaultSearchParams,
    defaultDataList = [],
    suspendedText,
    displayTextKey = 'label',
    displayText,
    displayOptions = [],
    displayTextType,
    searchKey,
    multiple = false,
    dataKey,
    listKey,
    emptyText,
    prefixIcon,
    prefixIconName,
    suffixIcon,
    suffixIconName,
    selectProps,

    // popup - props
    trigger = 'click',
    placement = 'bottom-left',
    width = 'auto',
    maxColumnWidth,
    minColumnWidth,
    overlayClassName,
    overlayInnerStyle,
    overlayStyle,

    // tag - input - props
    minCollapsedNum,
    collapsedItems,
    valueDisplay,
    collapsedToolTipProps,

    onReqEmpty,
    getFilterOptions,
    onFilter,
    getList,
    onClose,
    onClearable,
    onChange,
    onVisibleChange,
    label,
  } = props;

  const dropdownInputRef = useRef<HTMLDivElement>(null);
  const scrollListRef = useRef<any>(null);
  // 下拉项内容Ref
  const contentRef = useRef<any>(null);
  // 同步存储visible情况
  const visibleRef = useRef<any>(null);

  const [visible, setVisible] = useState<boolean>(defaultVisible);
  const [isResetSearch, setIsResetSearch] = useState<boolean>(false);
  const [isRefreshList, setIsRefreshList] = useState<boolean>(true);

  const [reqList, setReqList] = useState<{ text: string; value: any }[]>([]);

  const [popupWidth, setPopupWidth] = useState<any>(width);

  // 组件所用props
  const selectListProps = useMemo(
    () => ({
      listKey,
      dataKey,
      searchKey,
      displayTextKey,
      isAutoRequest,
      multiple,
      searchPlaceholder,
      emptyText,
      listHeader,
      footer,
      defaultSearchParams,
      defaultDataList,
      value,
      isResetSearch,
      searchable: searchable && displayTextType !== 'autoComplete',
      autofocus: visible,
      setIsResetSearch,
      getFilterOptions,
      onFilter,
      getList,
      onReqSuccess: (data: any[]) => setReqList((cData) => [...cData, ...data]),
      onChange,
      onClose: () => {
        setVisible(false);
        onClose?.();
      },
      onReqEmpty,
      ...selectProps,
    }),
    [
      visible,
      listKey,
      dataKey,
      searchKey,
      displayTextKey,
      isAutoRequest,
      multiple,
      searchPlaceholder,
      emptyText,
      listHeader,
      footer,
      defaultSearchParams,
      defaultDataList,
      value,
      isResetSearch,
      searchable,
      displayTextType,
      setIsResetSearch,
      getFilterOptions,
      onFilter,
      getList,
      onClose,
      onChange,
      setVisible,
      setReqList,
      onReqEmpty,
    ],
  );

  // 组件popup - props
  const customPopupProps = useMemo(() => {
    const config: any = {
      visible,
      trigger,
      placement,
      disabled,
    };
    // 当hover时，使用组件内置开启隐藏
    if (trigger === 'hover') {
      delete config.visible;
    }
    return config;
  }, [visible, trigger, placement, disabled]);

  // 显示文本
  const valueText = useMemo(() => {
    return isArrayLen(reqList)
      ? onSelectRenderText(reqList, value, {
          displayOptions,
        })
      : displayOptions;
  }, [value, reqList, displayOptions, displayTextKey]);

  // 刷新列表
  const onRefreshList = () => {
    setIsRefreshList(false);
    setTimeOut(() => {
      setIsRefreshList(true);
    }, 100);
  };

  // 监听 - 关闭
  // const onCloseListener = (event?: any) => {
  //   try {
  //     let flag = true;
  //     const domArr = [
  //       'dropdown-search-box',
  //       't-input',
  //       't-input__inner',
  //       'td-group-list',
  //       'td-scroll-list',
  //       'select-custom-components',
  //       // 'dropdown-footer',
  //       // 'go-page-button',
  //       // 't-link',
  //     ];
  //     for (const item of domArr) {
  //       if (event?.target?.closest(`.${item}`)) {
  //         flag = false;
  //         break;
  //       }
  //     }
  //     if (flag && visible) {
  //       setVisible(false);
  //       onClose?.();
  //     }
  //   } catch (e) {
  //     console.error('onClose =>>', e);
  //   }
  // };

  // 监听滚动，点击其他区域
  // useEffect(() => {
  //   // window.addEventListener('wheel', onCloseListener);
  //   window.addEventListener('mousedown', onCloseListener, true);
  //   return () => {
  //     // window.removeEventListener('wheel', onCloseListener);
  //     window.removeEventListener('mousedown', onCloseListener, true);
  //   };
  // }, [visible]);

  const shadowRender = useMemo(() => {
    return (
      visible && (
        <div
          className="popup-shadow"
          onClick={() => {
            setVisible(false);
            onClose?.();
          }}
        />
      )
    );
  }, [visible, setVisible, onClose]);

  useEffect(() => {
    if (defaultVisible) {
      setVisible(false);
    }
  }, [defaultVisible]);

  const popupOnVisibleChange = (
    visible: boolean,
    context: PopupVisibleChangeContext,
  ) => {
    if (matchButtonWidth) {
      const offsetWidth = dropdownInputRef.current?.offsetWidth || 0;
      if (offsetWidth > 0) {
        setPopupWidth(offsetWidth);
      }
    }
    onVisibleChange?.(visible);
  };

  // 监听点击下拉项范围外 - 执行关闭
  const onCloseListener = (event?: any) => {
    // 组件关闭 或 组件已被卸载，元素已不存在，则无需再向下执行关闭
    if (
      !visibleRef.current ||
      [contentRef?.current, dropdownInputRef?.current].includes(null)
    ) {
      return;
    }

    // 点击事件是否落下下拉项内
    const isClickListDom =
      contentRef?.current && contentRef.current.contains(event.target);
    // 点击事件是否落下触发展开下拉项Dom
    const isClickOpenDom =
      dropdownInputRef?.current &&
      dropdownInputRef?.current?.contains(event.target);

    // 关闭下拉逻辑
    if (!isClickListDom && !isClickOpenDom) {
      popupOnVisibleChange(false, {});
      setVisible(false);
      onClose?.();
    }
  };
  // 阻止滚动
  const preventScroll = (event: any) => event.preventDefault();

  useEffect(() => {
    visibleRef.current = visible;
    window.addEventListener('click', onCloseListener);
    const $layoutBodyDom: any = document.getElementsByClassName(
      'smartlink-layout__body',
    )[0];
    visible &&
      $layoutBodyDom &&
      $layoutBodyDom.addEventListener('wheel', preventScroll);

    return () => {
      $layoutBodyDom &&
        $layoutBodyDom.removeEventListener('wheel', preventScroll);
    };
  }, [visible]);

  // 绑定ref，抛出方法
  useImperativeHandle(refs, () => ({
    setVisible,
    setIsResetSearch,
    onRefreshList,
  }));

  return (
    <>
      <Popup
        {...customPopupProps}
        overlayClassName={classNames(
          'default-td-paging-select',
          {
            'ellipsis-list': ellipsis,
          },
          overlayClassName,
        )}
        overlayStyle={{
          marginTop: 8,
          ...overlayStyle,
        }}
        overlayInnerStyle={{
          width: popupWidth,
          minWidth: minColumnWidth,
          maxWidth: maxColumnWidth,
          marginTop: 0,
          ...overlayInnerStyle,
        }}
        content={
          <div ref={contentRef} onClick={(e) => e?.stopPropagation()}>
            {isRefreshList &&
              (isArrayLen(tabsList) ? (
                <Tabs>
                  {tabsList.map((item) => (
                    <Tabs.TabPanel label={item.label} value={item.value}>
                      <ScrollList
                        ref={scrollListRef}
                        {...selectListProps}
                        {...item.selectProps}
                        displayTextType={displayTextType}
                      />
                    </Tabs.TabPanel>
                  ))}
                </Tabs>
              ) : (
                <ScrollList ref={scrollListRef} {...selectListProps} />
              ))}
          </div>
        }
        onVisibleChange={popupOnVisibleChange}
      >
        <div
          ref={dropdownInputRef}
          // 当full的时候才100%，其他情况自适应
          className={classNames({
            'width-100': size === InputSizeEnum.FULL,
          })}
        >
          <SelectCustom
            label={label}
            width={width}
            className={className}
            suspendedText={suspendedText}
            displayText={displayText}
            displayTextKey={displayTextKey}
            displayTextType={displayTextType}
            visible={visible}
            value={value}
            valueText={valueText}
            placeholder={placeholder}
            disabled={disabled}
            clearable={clearable}
            borderless={borderless}
            multiple={multiple}
            autoWidth={autoWidth}
            size={size}
            prefixIcon={prefixIcon}
            prefixIconName={prefixIconName}
            suffixIcon={suffixIcon}
            suffixIconName={suffixIconName}
            minCollapsedNum={minCollapsedNum}
            collapsedItems={collapsedItems}
            valueDisplay={valueDisplay}
            collapsedToolTipProps={collapsedToolTipProps}
            searchPlaceholder={searchPlaceholder}
            autofocus={autofocus}
            onClick={() => {
              if (!disabled) {
                setVisible((val) => !val);
              }
            }}
            onClearable={() => {
              if (onClearable) {
                onClearable?.();
              }

              onChange?.(getDefaultValueByType(value), {
                trigger: 'clear',
              });
            }}
            onTagRemove={({ index }) => {
              const newValue = [...value];
              newValue.splice(index, 1);
              onChange?.(newValue, {
                trigger: 'tagRemove',
              });
            }}
            onSearch={(val) => {
              if (searchKey) {
                scrollListRef.current?.setSearchValue({
                  pageIndex: 1,
                  [searchKey]: val,
                });

                throttle(() => {
                  scrollListRef.current?.setIsSearch(true);
                }, 300)();
              }
            }}
          />
        </div>
      </Popup>
      {/* 阴影层 */}
      {shadowRender}
    </>
  );
}

export default forwardRef(PagingSelect);
